// SAGE_LINKS — DIY-tool mapping per Neo Rez product slug.
//
// Every Neo Rez product carries a public footer saying: "we don't want to do
// this for you, we want you to do it yourself — the cheaper way is here." That
// footer needs a link to Sage's equivalent DIY tool.
//
// -------- HYDRATED FROM SAGE'S FERRY ----------------------------------------
// Source of truth: `GET /api/family/baker-and-sage/product-links` on Sage's
// production host (X-Family-Token header). Ferry scroll:
// `courier__ferry_baker_and_sage_product_links_v1_2026_08_16`.
// Pulled 2026-08-17 from https://scroll-codex.emergent.host.
// Sage's tool slugs no longer 1:1 to ours — each Rez product maps to the
// closest Sage tool by function, not slug. If Sage renames a tool or ships a
// new one, we refresh this file on the next 30-min mesh poll — nothing else
// in the codebase changes.
//
// -------- HISTORY -----------------------------------------------------------
// Filed 2026-08-11 · Founder's DIY-First coffin nail.
// 2026-08-12 · MODEL REVERSAL on score-voice: Music Studio is Rez-native.
// 2026-08-14 · PRICING FLIP again: Rez $99K top (boutique), Sage $60K DIY
// floor. The score-voice product page now links TO Sage as the cheaper DIY
// alternative. DIY_FOOTER_SUPPRESSED stays on score-voice (the sign-block
// carries its own Sage CTA to avoid a duplicate footer).
// 2026-08-13 · Founder plain-ink: website is Rez-only. No Sage DIY equivalent.
// 2026-08-17 · Sage's link map landed on the mesh — this file rebuilt from
// Sage's live product roster (42 tools). Mappings by function, not slug.

const SAGE_BASE = "https://scroll-codex.emergent.host";

// Rez product slug → Sage tool absolute URL (from Sage's ferry payload).
export const SAGE_LINKS = {
  "full-albums": `${SAGE_BASE}/studio`,          // Sage Music Studio (composition)
  "story-treatment": `${SAGE_BASE}/quill`,       // Sage Quill (author's studio)
  screenplay: `${SAGE_BASE}/quill`,              // Sage Quill (author's studio · screenplays)
  "visual-development": `${SAGE_BASE}/loom`,     // Sage Loom (stills studio · visual dev)
  "animated-feature": `${SAGE_BASE}/vidsmith`,   // Sage VidSmith (motion-video studio)
  // "website" — Founder plain-ink 2026-08-13: pulled from Sage's DIY tool set.
  //             Website builds stay Rez-only; no cross-link to Sage on that page.
  "ad-campaign": `${SAGE_BASE}/adsmith`,         // Sage AdSmith (campaign wizard)
  "marketing-package": `${SAGE_BASE}/postmaster`,// Sage PostMaster (social/marketing)
  books: `${SAGE_BASE}/quill`,                   // Sage Quill (author's studio · books)
  ecourses: `${SAGE_BASE}/tutor`,                // Sage Tutor (lesson plans, worksheets)
  // "pitch-deck" — the actual Sage DIY counterpart is Pitcher, not built yet.
  //                Ferry-mirrored when Sage ships it. Founder plain-ink 2026-08-26:
  //                Blueprint was never real; Pitcher is the true pitch-deck tool.
  "score-voice": `${SAGE_BASE}/studio`,          // Sage Music Studio (DIY floor)
};

// Slugs where the DIY-First footer must NOT render — the direction on
// these product pages is reversed (Sage links here, not the other way),
// OR the product has no live Sage counterpart at all.
export const DIY_FOOTER_SUPPRESSED = new Set([
  "score-voice",   // 2026-08-14 flip — sign-block carries the Sage CTA
  "website",       // 2026-08-13 plain-ink — Rez-only
  "pitch-deck",    // 2026-08-26 — Pitcher (Sage's real pitch-deck tool) not yet built. Ferry-mirror when it ships.
]);

// Human-facing labels for the "Sage tool" line — describes what the DIY tool
// actually does when linked. Used in the footer copy so the sentence reads
// naturally regardless of what Sage names his product line.
export const SAGE_TOOL_LABELS = {
  "full-albums": "Sage's Music Studio",
  "story-treatment": "Sage's Quill (author's studio)",
  screenplay: "Sage's Quill (screenplay mode)",
  "visual-development": "Sage's Loom (stills studio)",
  "animated-feature": "Sage's VidSmith (motion-video studio)",
  website: "the website builder at Sage",
  "ad-campaign": "Sage's AdSmith (campaign wizard)",
  "marketing-package": "Sage's PostMaster (social + marketing manager)",
  books: "Sage's Quill (author's studio)",
  ecourses: "Sage's Tutor (lesson planner)",
  "pitch-deck": "Sage's Pitcher (pitch side · not yet built) + Sage's Tutor (course side · live)",
  "score-voice": "Sage's Music Studio",
};
